import {
  ROSTER_COMPLIANCE_CHOICES,
  ROSTER_PHONE_CHOICES,
  ROSTER_STATUS_CHOICES,
  type RosterFilterPreference,
  type StoredRosterFilter,
} from '@vigeo/shared';
import { statePresenters } from '@/features/readiness';
import { COMPLIANCE_NOT_TRACKED_LABEL, PHONE_LABEL, STATUS_LABEL, type ComplianceChoice, type PhoneChoice, type RosterFilters, type StatusChoice } from './rosterFilters.js';
import { complianceAndDocumentsShown } from '@/lib/sections';
import { NO_AREA } from './staff.js';

/**
 * What the roster remembers and saves (ADR-019, #476): the roster's filters WITHOUT the search box.
 *
 * `RosterFilters` holds the search text (`query`); `RosterFilterPreference` has no field for it, so
 * the text cannot reach a preferences request by this path. The name sort is not remembered either
 * (lead, 2026-10-05, under ADR-019: no schema field).
 */

const two = <T,>(chosen: readonly T[], all: number): readonly T[] => (chosen.length === all ? [] : chosen);

/** The four remembered parts. A two-way choice with both sides ticked means no filter, so it is stored as none. */
export function toPreference(filters: RosterFilters): RosterFilterPreference {
  return {
    areas: [...filters.areas],
    compliance: [...filters.compliance],
    statuses: [...two(filters.statuses, ROSTER_STATUS_CHOICES.length)],
    phones: [...two(filters.phones, ROSTER_PHONE_CHOICES.length)],
  };
}

/** Whether any of the four saved parts is on. Search text alone does not count (lead, 2026-10-05, decision C). */
export const preferenceIsOn = (p: RosterFilterPreference): boolean =>
  p.areas.length > 0 || p.compliance.length > 0 || p.statuses.length > 0 || p.phones.length > 0;

const sorted = (list: readonly string[]) => [...list].sort();

/** The same four parts, whatever order they were ticked in. */
export function samePreference(a: RosterFilterPreference, b: RosterFilterPreference): boolean {
  return (['areas', 'compliance', 'statuses', 'phones'] as const).every((part) => JSON.stringify(sorted(a[part])) === JSON.stringify(sorted(b[part])));
}

export interface AppliedStored {
  /** The four parts, with anything no longer allowed left out. */
  readonly parts: RosterFilterPreference;
  /** The stored choices that are no longer allowed, as the text that was stored. */
  readonly dropped: readonly string[];
  /** The filter had choices and every one of them is gone, so nothing should be applied. */
  readonly nothingLeft: boolean;
}

/** A stored Compliance choice in the words the Compliance filter uses, so a message names "Ready", not `ready` (#723). */
const complianceWords = (choice: string): string => (choice === 'not-tracked' ? COMPLIANCE_NOT_TRACKED_LABEL : (statePresenters as Readonly<Record<string, { readonly label: string } | undefined>>)[choice]?.label ?? choice);

/**
 * Reads a stored filter against what the roster allows today (ADR-019 §5). A choice that is no longer
 * allowed is left out and reported, never silently replaced and never silently showing everyone. A
 * service area is stale when nobody on the roster holds it now (`areaInUse` says so): the server only
 * accepts an area that is in use ("AR strict"), so one that is not is left out and said so, never kept
 * on screen to fail on the next write. Without `areaInUse`, no area is stale.
 */
export function readStored(stored: StoredRosterFilter, areaInUse: (area: string) => boolean = () => true): AppliedStored {
  const dropped: string[] = [];
  const keep = <T extends string>(values: readonly string[], allowed: readonly T[], labelOf: (value: string) => string): T[] =>
    values.filter((value): value is T => {
      if ((allowed as readonly string[]).includes(value)) return true;
      dropped.push(labelOf(value));
      return false;
    });
  const parts: RosterFilterPreference = {
    areas: stored.areas.filter((area) => {
      if (areaInUse(area)) return true;
      dropped.push(area === NO_AREA ? 'No service area' : area);
      return false;
    }),
    // Where there is no Compliance filter to see or change (#723), a stored choice is left out and said so, not applied unseen.
    compliance: keep<(typeof ROSTER_COMPLIANCE_CHOICES)[number]>(stored.compliance, complianceAndDocumentsShown() ? ROSTER_COMPLIANCE_CHOICES : [], complianceWords),
    statuses: keep<StatusChoice>(stored.statuses, ROSTER_STATUS_CHOICES, (v) => v),
    phones: keep<PhoneChoice>(stored.phones, ROSTER_PHONE_CHOICES, (v) => v),
  };
  const had = stored.areas.length + stored.compliance.length + stored.statuses.length + stored.phones.length;
  return { parts: { ...parts, statuses: [...two(parts.statuses, ROSTER_STATUS_CHOICES.length)], phones: [...two(parts.phones, ROSTER_PHONE_CHOICES.length)] }, dropped, nothingLeft: had > 0 && dropped.length > 0 && !preferenceIsOn(parts) };
}

/**
 * The filters with a stored filter's four parts in place of the current four, and the search box
 * exactly as it is (decision B).
 */
export function withParts(current: RosterFilters, parts: RosterFilterPreference): RosterFilters {
  // The place selection is not one of the four parts (ADR-019 keeps typed areas only), so applying a saved filter leaves it as it
  // was: the chosen places, and the "no place" choice that is part of the same selection (#689). Both come from `current` and
  // never from `parts`, whose type has no field for either, so nothing stored can turn one on.
  return {
    query: current.query,
    ...(current.places === undefined ? {} : { places: current.places }),
    ...(current.noPlace === undefined ? {} : { noPlace: current.noPlace }),
    // The language choice is held for this visit only too (#772), so applying a saved filter leaves it as it was.
    ...(current.languages === undefined ? {} : { languages: current.languages }),
    // So is the job title choice (#804).
    ...(current.jobTitles === undefined ? {} : { jobTitles: current.jobTitles }),
    areas: parts.areas, compliance: parts.compliance as readonly ComplianceChoice[], statuses: parts.statuses as readonly StatusChoice[], phones: parts.phones as readonly PhoneChoice[],
  };
}

/** "This saved filter used X and Y, which no longer exist. It was left out." The ADR's words, with the subject varying. */
export function staleMessage(subject: 'saved' | 'last', dropped: readonly string[], nothingLeft: boolean): string {
  const list = dropped.length === 1 ? (dropped[0] ?? '') : `${dropped.slice(0, -1).join(', ')} and ${dropped[dropped.length - 1] ?? ''}`;
  const used = subject === 'saved' ? 'This saved filter used' : 'Your filters from last time used';
  const tail = nothingLeft ? 'Nothing was applied.' : dropped.length === 1 ? 'It was left out.' : 'They were left out.';
  return `${used} ${list}, which no longer ${dropped.length === 1 ? 'exists' : 'exist'}. ${tail}`;
}

/** One line for a saved filter in the list: "Service area: Roanoke · Compliance: Ready". */
export function describePreference(p: StoredRosterFilter, complianceLabelOf: (choice: string) => string): string {
  const parts: string[] = [];
  if (p.areas.length > 0) parts.push(`Service area: ${p.areas.map((a) => (a === NO_AREA ? 'No service area' : a)).join(', ')}`);
  if (p.compliance.length > 0) parts.push(`Compliance: ${p.compliance.map(complianceLabelOf).join(', ')}`);
  const status = two(p.statuses, ROSTER_STATUS_CHOICES.length);
  if (status.length > 0) parts.push(`Active or inactive: ${status.map((s) => STATUS_LABEL[s as StatusChoice] ?? s).join(', ')}`);
  const phone = two(p.phones, ROSTER_PHONE_CHOICES.length);
  if (phone.length > 0) parts.push(`Phone: ${phone.map((s) => PHONE_LABEL[s as PhoneChoice] ?? s).join(', ')}`);
  return parts.length === 0 ? 'No filters' : parts.join(' · ');
}

/** "X no longer exists. It was left out." (ADR-019 §5) for service areas nobody on the roster holds now. */
export function areaGoneMessage(areas: readonly string[]): string {
  const list = areas.length === 1 ? (areas[0] ?? '') : `${areas.slice(0, -1).join(', ')} and ${areas[areas.length - 1] ?? ''}`;
  return areas.length === 1 ? `${list} no longer exists. It was left out.` : `${list} no longer exist. They were left out.`;
}
